import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { ExpandLess, ExpandMore, Search } from "@mui/icons-material";
import theme from "../../../style/theme";

/** テーブルのスタイル(セル)を設定 */
export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: "nowrap", // 改行しない
    padding: "6px 12px",
  },
  [`&.${tableCellClasses.body}`]: { padding: "6px 12px" },
}));

/** テーブルのスタイル(行)を設定 */
export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

/** ページングコンポーネント */
const PagingComponent = ({
  position,
  sortAndFilteredDataSize,
  page,
  setPage,
  pageSize,
}: {
  position: "top" | "bottom";
  sortAndFilteredDataSize: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
}) => {
  return (
    <Box
      sx={{
        ml: 2,
        mt: position === "top" ? 1 : "auto",
        mb: 1,
        display: "flex",
        gap: 2,
        justifyContent: "flex-start",
        alignItems: "center",
      }}
    >
      <div style={{ marginTop: 1, color: "#666", fontSize: "1rem" }}>
        全 {sortAndFilteredDataSize} 件中{" "}
        {sortAndFilteredDataSize === 0
          ? "0 件 〜 0 件"
          : `${(page - 1) * pageSize + 1} 件 〜 ${Math.min(
              page * pageSize,
              sortAndFilteredDataSize
            )} 件`}
        を表示
      </div>
      <div>
        <Pagination
          count={Math.ceil(sortAndFilteredDataSize / pageSize)}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          sx={{
            "& .MuiPaginationItem-root": {
              minWidth: "20px",
              height: "20px",
              padding: "1px",
            },
          }}
        />
      </div>
    </Box>
  );
};

/** ソートの型定義 */
type SortConfigType = {
  key: string;
  asc: boolean;
};

/** デフォルトの表示件数 */
const defaultPageSize = 50;

/** 表示件数の選択肢 */
const pageSizeList = [50, 100, 1000];

/** 一覧テーブル表示コンポーネントProps */
type CustomTableComponentProps = {
  name: string; // テーブル名
  rows: any[]; // テーブルデータ
  columnLabels: Partial<Record<string, string>>; // 列ラベル
  getDataStr: (row: any) => Partial<Record<string, string>>; // 各列を文字列として取得
  handleClickRow: (row: any) => void; // 行クリック時の処理
  cellRenderer: (row: any, key: string) => React.JSX.Element; // 各行セルレンダー
  customButtonCellRenderer: () => React.JSX.Element; // 表示設定・検索欄とテーブル欄の間に配置するボタン群
  customHeaderRenderer?: () => React.JSX.Element; // 選択チェックボックス・表示列以外のヘッダー部分
  selectedIdName?: string; // 選択行を特定するキー(id等)
  selectedRows?: Set<string>; // 選択行
  setSelectedRows?: React.Dispatch<React.SetStateAction<Set<string>>>; // 選択行設定イベント
};

/**
 * 汎用一覧テーブル表示コンポーネント
 * - 検索・列別検索・ソート・ページング・表示項目設定を備えた再利用可能なテーブルコンポーネント
 * - セルのレンダリングは呼び出し側で定義したcellRendererに委譲する
 * - props: name (テーブル識別名), rows (テーブルデータ), columnLabels (列ラベル定義),
 *          getDataStr (検索用文字列変換), handleClickRow (行クリックハンドラー),
 *          cellRenderer (セルレンダラー), customButtonCellRenderer (ボタン領域レンダラー)
 */
export default function CustomTableComponent({
  name,
  rows,
  columnLabels,
  getDataStr,
  handleClickRow,
  cellRenderer,
  selectedIdName,
  selectedRows,
  setSelectedRows,
  customButtonCellRenderer,
  customHeaderRenderer,
}: CustomTableComponentProps) {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const [searchTerm, setSearchTerm] = useState(
    sessionStorage.getItem(`searchText_${name}`) || ""
  ); // sessionStorageに値がある場合はその値を格納。ない場合は空文字を格納。
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
    const stored = sessionStorage.getItem(`visibleColomns_${name}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      } catch (e) {
        console.warn("visibleColumns の復元に失敗しました:", e);
      }
    }
    return new Set<string>(Object.keys(columnLabels));
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [pageSize, setPageSize] = useState(
    Number(sessionStorage.getItem(`showItems_${name}`)) || defaultPageSize
  ); // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  const [page, setPage] = useState(() => {
    // sessionStorageに値がある場合はその値を格納。それ以外は1をセットする。
    const stored = sessionStorage.getItem(`paging_${name}`);
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });
  const [showSearchSettings, setShowSearchSettings] = useState(false);
  const [columnSearchTerms, setColumnSearchTerms] = useState<
    Record<string, string>
  >({});
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfigType>({
    key: "code",
    asc: true,
  });

  /** 表示項目設定 */
  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem(
      `visibleColomns_${name}`,
      JSON.stringify(Array.from(newSet))
    ); // 表示するカラム情報をsessionStorageに格納する。
    setVisibleColumns(newSet);
  };

  /** フィルターの条件を初期化 */
  const filterReset = () => {
    // sessionStorageを削除する
    sessionStorage.removeItem(`searchText_${name}`);
    sessionStorage.removeItem(`showItems_${name}`);
    sessionStorage.removeItem(`visibleColomns_${name}`);
    sessionStorage.removeItem(`columnSearchTerms_${name}`);
    sessionStorage.removeItem(`paging_${name}`);

    // フィルター条件の初期化
    setSearchTerm("");
    setVisibleColumns(new Set(Object.keys(columnLabels)));
    setPageSize(defaultPageSize);
    setColumnSearchTerms({});
    setPage(1);
  };

  /** 検索設定変更時のフィルタリング処理 */
  const handleColumnSearchChange = useCallback(
    (key: string, value: string) => {
      const updated = {
        ...columnSearchTerms,
        [key]: value,
      };
      setColumnSearchTerms(updated);
      // すべての値が空文字なら sessionStorage を削除
      Object.values(updated).every((v) => v === "")
        ? sessionStorage.removeItem(`columnSearchTerms_${name}`)
        : sessionStorage.setItem(
            `columnSearchTerms_${name}`,
            JSON.stringify(updated)
          );
    },
    [columnSearchTerms, name]
  );

  /** データの検索結果をメモ化 */
  const filteredData = useMemo(() => {
    let result = [...rows];
    // 全体検索キーワード（検索欄）
    const keywords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length > 0) {
      result = result.filter((row) =>
        keywords.every((keyword) =>
          Object.values(getDataStr(row)).some((value) =>
            value?.toLowerCase().includes(keyword)
          )
        )
      );
    }
    // 項目別検索
    const activeColumnSearches = Object.entries(columnSearchTerms).filter(
      ([_, value]) => value.trim() !== ""
    );
    if (activeColumnSearches.length > 0) {
      result = result.filter((row) =>
        activeColumnSearches.every(([key, value]) =>
          (row[key] as string).toLowerCase().includes(value.toLowerCase())
        )
      );
    }
    setPage(1);
    return result;
  }, [rows, searchTerm, columnSearchTerms, getDataStr]);

  /** ソート&フィルター機能 */
  const sortAndFilteredData = useMemo(() => {
    let result = [...filteredData];
    const { key, asc } = sortConfig ?? {};
    result.sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];
      return asc
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return result;
  }, [filteredData, sortConfig]);

  /** ページング機能 */
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return sortAndFilteredData.slice(startIndex, startIndex + pageSize);
  }, [sortAndFilteredData, page, pageSize]);

  useEffect(() => {
    // 検索設定に関してsessionStorageに値がある場合はその値を反映する。
    const savedTerms = sessionStorage.getItem(`columnSearchTerms_${name}`);
    if (savedTerms) {
      try {
        const parsedTerms = JSON.parse(savedTerms);
        if (typeof parsedTerms === "object" && parsedTerms !== null) {
          const sanitizedTerms = Object.fromEntries(
            Object.entries(parsedTerms).filter(
              ([_, v]) => typeof v === "string" && v !== ""
            )
          ) as Record<string, string>;
          setColumnSearchTerms(sanitizedTerms);
        }
      } catch (e) {
        console.warn("Invalid sessionStorage value for columnSearchTerms");
      }
    }
  }, [name]);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2}>
        <div className="mb-2 flex items-center gap-2">
          <TextField
            label="検索"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setSearchTerm(value);
              // 空の場合はsessionStorageを削除。それ以外は値を格納する。
              value === ""
                ? sessionStorage.removeItem("searchText_UserManagement")
                : sessionStorage.setItem("searchText_UserManagement", value);
            }}
            className="w-64"
            sx={{ ml: 1, mt: 2 }}
          />
        </div>
        <Chip
          label="リセット"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            ml: 1,
            mt: 2,
          }}
          onClick={filterReset}
        />
      </Box>
      <Button
        sx={{ ml: 1, mt: 1 }}
        onClick={() => setShowColumnSettings(!showColumnSettings)}
        startIcon={showColumnSettings ? <ExpandLess /> : <ExpandMore />}
      >
        表示設定
      </Button>
      <Box sx={{ pl: 2, pb: 1 }}>
        {showColumnSettings && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <Box sx={{ mb: 0 }}>
              <div
                style={{
                  fontWeight: "bold",
                  color: "grey",
                  marginBottom: -6,
                }}
              >
                表示件数:
              </div>
              <RadioGroup
                row
                value={pageSize.toString()}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  sessionStorage.setItem(
                    "showItems_UserManagement",
                    e.target.value
                  ); // 表示設定が変わったら、sessionStorageに値を格納する。
                  setPage(1);
                  sessionStorage.removeItem("paging_UserManagement"); // 表示設定が変わったら、sessionStorageの値を削除する。
                }}
              >
                {pageSizeList.map((num) => (
                  <FormControlLabel
                    key={num}
                    value={num.toString()}
                    control={<Radio sx={{ transform: "scale(0.6)" }} />}
                    label={`${num}件`}
                  />
                ))}
              </RadioGroup>
            </Box>
            <Box>
              <div
                className="font-semibold mb-2"
                style={{
                  fontWeight: "bold",
                  color: "grey",
                  marginBottom: 4,
                }}
              >
                表示項目:
              </div>
              <Box className="flex flex-wrap gap-2">
                {Object.entries(columnLabels).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={visibleColumns.has(key)}
                      onChange={() => toggleColumn(key)}
                    />
                    {label}
                  </label>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Button
        sx={{ ml: 1, mt: -1 }}
        onClick={() => setShowSearchSettings(!showSearchSettings)}
        startIcon={showSearchSettings ? <ExpandLess /> : <ExpandMore />}
      >
        検索設定
      </Button>
      <Box sx={{ pl: 2, pb: 2 }}>
        {showSearchSettings && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {Object.entries(columnLabels)
              .filter(([key]) => key !== "actions")
              .map(([key, label]) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 100,
                  }}
                >
                  <div style={{ fontSize: "1rem", marginBottom: 4 }}>
                    {label}
                  </div>
                  <TextField
                    size="small"
                    variant="outlined"
                    value={columnSearchTerms[key] || ""}
                    onChange={(e) =>
                      handleColumnSearchChange(key, e.target.value)
                    }
                    sx={{
                      width: 140,
                      "& input": { padding: "4px 8px" },
                    }}
                    slotProps={{
                      input: {
                        startAdornment: columnSearchTerms[key] ? undefined : (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
              ))}
          </Box>
        )}
      </Box>
      <Box sx={{ ml: 1 }}>{customButtonCellRenderer()}</Box>
      <PagingComponent
        position="top"
        sortAndFilteredDataSize={sortAndFilteredData.length}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
      />
      <TableContainer
        component={Paper}
        sx={{
          mx: isMobile ? 0 : 1,
          mb: 2,
          overflowX: "auto", // 横方向でスクロールを表示
          maxWidth: isMobile ? "100%" : "calc(100vw - 80px)",
        }}
      >
        <Table
          sx={{ tableLayout: "auto", minWidth: 800 }}
          aria-label="customized table"
        >
          <TableHead>
            <TableRow>
              {selectedRows && setSelectedRows && selectedIdName && (
                <StyledTableCell>
                  <Tooltip
                    title={
                      selectedRows.size === rows.length
                        ? "すべて選択解除"
                        : "すべて選択"
                    }
                  >
                    <Checkbox
                      checked={selectedRows.size === rows.length}
                      onChange={() =>
                        setSelectedRows(
                          selectedRows.size === rows.length
                            ? new Set()
                            : new Set(rows.map((row) => row.userId ?? ""))
                        )
                      }
                    />
                  </Tooltip>
                </StyledTableCell>
              )}
              {customHeaderRenderer && customHeaderRenderer()}
              {Object.entries(columnLabels).map(([key, label]) => {
                const isActive = sortConfig?.key === key;
                const asc = sortConfig?.asc;
                return visibleColumns.has(key) ? (
                  <StyledTableCell
                    key={key}
                    onClick={() => {
                      setPage(1); // 第一ページに戻る
                      setSortConfig(
                        sortConfig?.key === key
                          ? {
                              key: key,
                              asc: !sortConfig.asc,
                            }
                          : {
                              key: key,
                              asc: true,
                            }
                      );
                    }}
                    onMouseEnter={() => setHoveredColumn(key)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      {label}
                      {(isActive || hoveredColumn === key) &&
                        (asc && isActive ? (
                          <ExpandLess
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : !asc && isActive ? (
                          <ExpandMore
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : (
                          <ExpandMore
                            fontSize="small"
                            className="ml-1 text-gray-300"
                          />
                        ))}
                    </div>
                  </StyledTableCell>
                ) : null;
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <StyledTableRow
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#e3f2fd", // 浅いブルー
                    boxShadow: "inset 0 0 0 1px #90caf9",
                  },
                }}
                onClick={() => handleClickRow(row)}
              >
                {Object.keys(columnLabels)
                  .filter((key) => visibleColumns.has(key))
                  .map((key) => (
                    <>
                      {selectedRows && setSelectedRows && selectedIdName && (
                        <StyledTableCell sx={{ verticalAlign: "top" }}>
                          <Checkbox
                            checked={selectedRows.has(
                              row[selectedIdName] ?? ""
                            )}
                            onChange={(e) => {
                              e.stopPropagation(); // クリックイベントを防止
                              const newSet = new Set(selectedRows);
                              newSet.has(row[selectedIdName] ?? "")
                                ? newSet.delete(row[selectedIdName] ?? "")
                                : newSet.add(row[selectedIdName] ?? "");
                              setSelectedRows(newSet);
                            }}
                          />
                        </StyledTableCell>
                      )}
                      {cellRenderer(row, key)}
                    </>
                  ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PagingComponent
        position="bottom"
        sortAndFilteredDataSize={sortAndFilteredData.length}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
      />
    </Box>
  );
}
